import { NextSeo } from "next-seo";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { GetStaticProps } from "next";
import { Routes } from "~/routes";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { BroadridgeIndividualPage } from "~/modules/platforms/pages/broadridge_individual";
import {
    getBroadridgePageDescription,
    BroadridgeIndividualSEO,
} from "~/modules/app/seo/BroadridgeIndividualSEO";
import { Platform } from "~/modules/platforms";
import { queryPlatformSnapshots } from "~/modules/platforms/api/router";
import { PlatformSnapshot } from "~/modules/platforms/pages/broadridge_individual/facts_platform_snapshots_data";
import { SOCIAL_OG_IMAGE_URL, TOKENIZATION_TYPE_IDS } from "~/consts";

// // // //

export const getStaticProps: GetStaticProps<{
    platform: Platform;
    snapshots: PlatformSnapshot[];
}> = async (ctx) => {
    const req = await queryPlatformSnapshots();
    const snapshots = req.data.results;

    // const BROADRIDGE_PLATFORM_ID = 423;

    // const [broadridgeRes] = await Promise.all([
    //     queryPlatforms({
    //         query: {
    //             ...DEFAULT_PLATFORM_LIST_QUERY,
    //             filter: {
    //                 field: PlatformColumns.id,
    //                 operator: FilterOperators.Equals,
    //                 value: BROADRIDGE_PLATFORM_ID,
    //             },
    //         },
    //     }),
    // ]);

    // const platform = broadridgeRes.results[0];

    // if (!platform) {
    //     return {
    //         props: {
    //             platform: null,
    //         },
    //     };
    // }

    // // // //

    // Hardcoded Broadridge Platform Data
    const platform: Platform = {
        id: 423,
        name: "Broadridge DLR",
        website:
            "https://www.broadridge.com/capability/middle-and-back-office-solutions/post-trade-processing/distributed-ledger-repo-solutions",
        icon_url:
            "https://rwa-partners-infra-prod-rwapartnersuploads740d2985-1c2klgo7i3ef.s3.us-east-1.amazonaws.com/uploads/images/Screenshot%202025-07-24%20at%2010.43.40%E2%80%AFAM.png",
        description:
            "Broadridge’s Distributed Ledger Repo (DLR) is the leading distributed ledger solution transforming global repo market infrastructure. DLR leverages tokenization and smart contracts to increase collateral velocity, improve liquidity management, and reduce costs of trade processing while interoperating within traditional and blockchain-based market infrastructure. DLR runs on Canton technology.",
        tokenization_type: null,
        asset_classes: null,
        sectors: null,
        regions: null,
        inception_date: null,
        networks: null,
        color_hex: null,
        claimed_profile: null,
        _updated_at: null,
        slug: "broadridge-dlr",
        asset_count: 0,
        token_count: null,
        asset_class_ids: [],
        network_ids: [],
        issuer_ids: [],
        jurisdiction_country_ids: [],
        asset_class_stats: [],
        tokenization_type_stats: [],
        network_stats: [],
        issuer_stats: [],
        jurisdiction_country_stats: [],
        protocol_id: null,
        bridged_token_market_cap_dollar: null,
        bridged_token_value_dollar: null,
        holding_addresses_count: null,
        daily_mints_token: null,
        daily_mints_dollar: null,
        adjusted_daily_mints_dollar: null,
        adjusted_daily_mints_token: null,
        trailing_30_day_active_addresses_count: null,
        circulating_asset_value_dollar: null,
        trailing_30_day_transfer_volume: null,
        tokenization_type_id: null,
        issuance_type: null,
        tokenization_type_ids: [TOKENIZATION_TYPE_IDS.OnChainRepresented],
    };

    // // // //

    return {
        props: {
            platform: {
                ...platform,
                // TODO - remove these hardcoded values
                website_url:
                    "https://www.broadridge.com/capability/middle-and-back-office-solutions/post-trade-processing/distributed-ledger-repo-solutions",
                icon_url:
                    "https://rwa-partners-infra-prod-rwapartnersuploads740d2985-1c2klgo7i3ef.s3.us-east-1.amazonaws.com/uploads/images/Screenshot%202025-07-24%20at%2010.43.40%E2%80%AFAM.png",
            },
            snapshots,
        },
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
};

// // // //

interface Props {
    platform: Platform;
    snapshots: PlatformSnapshot[];
}

export default function BroadridgePage(props: Props) {
    const { platform, snapshots } = props;

    return (
        <PageTrack route={Routes.broadridge}>
            <NextSeo
                title={platform.name}
                canonical={addBaseUrl(Routes.broadridge)}
                description={getBroadridgePageDescription()}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.broadridge),
                    title: platform.name,
                    site_name: "RWA.xyz",
                    description: getBroadridgePageDescription(),
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
            />
            <BroadridgeIndividualSEO />
            <BreadcrumbSEO
                items={[
                    {
                        name: "Broadridge DLR",
                        href: Routes.broadridge,
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => <Navbar route={Routes.broadridge} user={user} />}
            </UserFetcher>
            <SidebarLayout pathname={Routes.broadridge}>
                <Breadcrumbs
                    pages={[
                        {
                            name: "Platforms",
                            href: Routes.platforms,
                        },
                        {
                            name: "Broadridge DLR",
                            current: true,
                        },
                    ]}
                />
                <PageWrapper>
                    <BroadridgeIndividualPage
                        platform={platform}
                        snapshots={snapshots}
                    />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
